import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Tooltip,
  Skeleton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Apps as AppsIcon,
  Person as PersonIcon,
  Code as CodeIcon,
  Web as WebIcon,
  Api as ApiIcon,
  Public as PublicIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  VpnKey as VpnKeyIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { useAppCache } from '../context/AppCacheContext';

interface ApplicationDashboardProps {
  onFilterSelect: (filterType: "ALL" | "OWNED" | "SPA" | "WEB" | "API" | "SINGLE_TENANT" | "MULTI_TENANT" | "WITH_SECRETS" | "SAML") => void;
}

interface MetricCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ReactElement;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  onClick?: () => void;
  loading?: boolean;
  trend?: 'up' | 'down' | 'neutral';
  badge?: string;
}

const MetricCard: React.FC<MetricCardProps> = React.memo(({
  title,
  value,
  subtitle,
  icon,
  color = 'primary',
  onClick,
  loading = false,
  trend,
  badge
}) => {
  const theme = useTheme();
  
  const cardColors = {
    primary: { bg: theme.palette.primary.light, text: theme.palette.primary.dark },
    secondary: { bg: theme.palette.secondary.light, text: theme.palette.secondary.dark },
    success: { bg: theme.palette.success.light, text: theme.palette.success.dark },
    warning: { bg: theme.palette.warning.light, text: theme.palette.warning.dark },
    error: { bg: theme.palette.error.light, text: theme.palette.error.dark },
    info: { bg: theme.palette.info.light, text: theme.palette.info.dark }
  };

  const colorScheme = cardColors[color];

  if (loading) {
    return (
      <Card 
        sx={{ 
          height: 120,
          cursor: 'default',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="text" width="60%" height={20} sx={{ ml: 1 }} />
          </Box>
          <Skeleton variant="text" width="40%" height={32} />
          <Skeleton variant="text" width="80%" height={16} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      sx={{ 
        height: 120,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid',
        borderColor: 'divider',
        position: 'relative',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: 4,
          borderColor: colorScheme.bg
        } : {},
        background: `linear-gradient(135deg, ${colorScheme.bg}15 0%, ${colorScheme.bg}08 100%)`
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ 
              p: 0.5, 
              borderRadius: 1, 
              bgcolor: `${colorScheme.bg}30`,
              color: colorScheme.text,
              mr: 1
            }}>
              {React.cloneElement(icon, { fontSize: 'small' })}
            </Box>
            <Typography 
              variant="subtitle2" 
              color="textSecondary"
              sx={{ 
                fontSize: '0.75rem',
                fontWeight: 500,
                letterSpacing: '0.02em'
              }}
            >
              {title}
            </Typography>
          </Box>
          
          {trend && (
            <TrendingUpIcon 
              sx={{ 
                fontSize: '1rem', 
                color: trend === 'up' ? 'success.main' : trend === 'down' ? 'error.main' : 'text.secondary',
                transform: trend === 'down' ? 'rotate(180deg)' : 'none'
              }} 
            />
          )}
        </Box>
        
        <Typography 
          variant="h3" 
          sx={{ 
            fontWeight: 700,
            color: colorScheme.text,
            fontSize: '1.75rem',
            lineHeight: 1.2,
            mb: 0.5
          }}
        >
          {value}
        </Typography>
        
        {subtitle && (
          <Typography 
            variant="caption" 
            color="textSecondary"
            sx={{ 
              fontSize: '0.6875rem',
              display: 'block',
              lineHeight: 1.2
            }}
          >
            {subtitle}
          </Typography>
        )}
        
        {badge && (
          <Chip
            label={badge}
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              height: 18,
              fontSize: '0.625rem',
              bgcolor: `${colorScheme.bg}40`,
              color: colorScheme.text,
              border: 'none'
            }}
          />
        )}
      </CardContent>
    </Card>
  );
});

// Custom hook for dashboard metrics using AppCacheContext
const useDashboardMetrics = () => {
  const { 
    applications, 
    appOwners, 
    currentUserId, 
    spMap, 
    loading, 
    loadingOwners 
  } = useAppCache();

  return useMemo(() => {
    if (loading || applications.length === 0) {
      return {
        total: 0,
        owned: 0,
        spa: 0,
        web: 0,
        api: 0,
        singleTenant: 0,
        multiTenant: 0,
        withSecrets: 0,
        samlApps: 0,
        securityScore: 0,
        loading: true,
        loadingOwners
      };
    }

    const total = applications.length;
    
    // Count owned applications using cached ownership data
    const owned = currentUserId ? applications.filter(app => {
      if (!app.appId) return false;
      const ownerInfo = appOwners[app.appId];
      return ownerInfo ? ownerInfo.owners.some(owner => owner.id === currentUserId) : false;
    }).length : 0;

    // Count by application type
    const spa = applications.filter(app => app.spa?.redirectUris && app.spa.redirectUris.length > 0).length;
    const web = applications.filter(app => app.web?.redirectUris && app.web.redirectUris.length > 0).length;
    const api = applications.filter(app => app.api?.oauth2PermissionScopes && app.api.oauth2PermissionScopes.length > 0).length;

    // Count by tenant type
    const singleTenant = applications.filter(app => app.signInAudience === 'AzureADMyOrg').length;
    const multiTenant = applications.filter(app => 
      app.signInAudience && ['AzureADMultipleOrgs', 'AzureADandPersonalMicrosoftAccount'].includes(app.signInAudience)
    ).length;

    // Security metrics
    const withSecrets = applications.filter(app => 
      app.passwordCredentials && app.passwordCredentials.length > 0
    ).length;
    
    const samlApps = applications.filter(app => 
      app.appId && spMap[app.appId] === "saml"
    ).length;

    // Calculate security score (simplified)
    const appsWithCredentials = applications.filter(app => 
      (app.passwordCredentials && app.passwordCredentials.length > 0) ||
      (app.keyCredentials && app.keyCredentials.length > 0)
    ).length;
    const securityScore = total > 0 ? Math.round((appsWithCredentials / total) * 100) : 0;

    return {
      total,
      owned,
      spa,
      web,
      api,
      singleTenant,
      multiTenant,
      withSecrets,
      samlApps,
      securityScore,
      loading: false,
      loadingOwners
    };
  }, [applications, appOwners, currentUserId, spMap, loading, loadingOwners]);
};

const ApplicationDashboard: React.FC<ApplicationDashboardProps> = ({
  onFilterSelect
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Use the custom hook that leverages AppCacheContext
  const metrics = useDashboardMetrics();
  const { currentUserId } = useAppCache();

  // Event handlers
  const handleMetricClick = useCallback((filterType: "ALL" | "OWNED" | "SPA" | "WEB" | "API" | "SINGLE_TENANT" | "MULTI_TENANT" | "WITH_SECRETS" | "SAML") => {
    onFilterSelect(filterType);
  }, [onFilterSelect]);

  // Responsive grid configuration
  const getGridSize = (index: number) => {
    if (isMobile) {
      return { xs: 6 }; // 2 cards per row on mobile
    }
    return { xs: 12, sm: 6, md: 4, lg: 3 }; // Responsive sizing
  };

const metricCards = useMemo(() => [
  {
    title: "Total Applications",
    value: metrics.total,
    subtitle: "All registered apps",
    icon: <AppsIcon />,
    color: 'primary' as const,
    onClick: () => handleMetricClick("ALL"),
    loading: metrics.loading
  },
  {
    title: "Single Tenant",
    value: metrics.singleTenant,
    subtitle: `${metrics.total > 0 ? Math.round((metrics.singleTenant / metrics.total) * 100) : 0}% of total`,
    icon: <BusinessIcon />,
    color: 'secondary' as const,
    onClick: () => handleMetricClick("SINGLE_TENANT"),
    loading: metrics.loading
  },
  {
    title: "Multi Tenant",
    value: metrics.multiTenant,
    subtitle: `${metrics.total > 0 ? Math.round((metrics.multiTenant / metrics.total) * 100) : 0}% of total`,
    icon: <PublicIcon />,
    color: 'warning' as const,
    onClick: () => handleMetricClick("MULTI_TENANT"),
    loading: metrics.loading
  },
  {
    title: "SPA Applications",
    value: metrics.spa,
    subtitle: "Single-page apps",
    icon: <CodeIcon />,
    color: 'success' as const,
    onClick: () => handleMetricClick("SPA"),
    loading: metrics.loading
  },
  {
    title: "Web Applications",
    value: metrics.web,
    subtitle: "Traditional web apps",
    icon: <WebIcon />,
    color: 'info' as const,
    onClick: () => handleMetricClick("WEB"),
    loading: metrics.loading
  },
  {
    title: "SAML Apps",
    value: metrics.samlApps,
    subtitle: "Apps using SAML SSO",
    icon: <VpnKeyIcon />,
    color: 'info' as const,
    onClick: () => handleMetricClick("SAML"),
    loading: metrics.loading
  },
  {
    title: "API Applications",
    value: metrics.api,
    subtitle: "Apps exposing scopes",
    icon: <ApiIcon />,
    color: 'secondary' as const,
    onClick: () => handleMetricClick("API"),
    loading: metrics.loading
  },
  {
    title: "With Client Secrets",
    value: metrics.withSecrets,
    subtitle: "Apps with credentials",
    icon: <VpnKeyIcon />,
    color: (metrics.withSecrets > metrics.total * 0.7 ? 'success' : 'warning') as 'success' | 'warning',
    onClick: () => handleMetricClick("WITH_SECRETS"),
    badge: metrics.withSecrets > 0 ? 'Secured' : undefined,
    loading: metrics.loading
  }
], [metrics, currentUserId, handleMetricClick]);

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <SecurityIcon sx={{ mr: 1, color: 'text.secondary' }} />
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            color: 'text.primary',
            letterSpacing: '0.02em'
          }}
        >
          Application Portfolio Overview
        </Typography>
        <Tooltip title="Dashboard provides quick insights into your application portfolio. Click on any metric to filter the application list.">
          <InfoIcon sx={{ ml: 1, fontSize: '1rem', color: 'text.secondary', cursor: 'help' }} />
        </Tooltip>
      </Box>
      
      <Grid container spacing={2}>
        {metricCards.map((card, index) => (
          <Grid item key={card.title} {...getGridSize(index)}>
            <MetricCard {...card} />
          </Grid>
        ))}
      </Grid>

      {/* Security Summary Bar */}
      {!metrics.loading && metrics.total > 0 && (
        <Box sx={{ 
          mt: 2, 
          p: 2, 
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              Security Overview:
            </Typography>
            <Chip
              label={`${metrics.securityScore}% apps with credentials`}
              size="small"
              color={metrics.securityScore > 70 ? 'success' : metrics.securityScore > 40 ? 'warning' : 'error'}
              sx={{ ml: 1 }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Types:
            </Typography>
            {metrics.spa > 0 && (
              <Chip label={`${metrics.spa} SPA`} size="small" variant="outlined" />
            )}
            {metrics.web > 0 && (
              <Chip label={`${metrics.web} Web`} size="small" variant="outlined" />
            )}
            {metrics.api > 0 && (
              <Chip label={`${metrics.api} API`} size="small" variant="outlined" />
            )}
            {metrics.samlApps > 0 && (
              <Chip label={`${metrics.samlApps} SAML`} size="small" variant="outlined" />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ApplicationDashboard;